'use client';

import type { NextPage } from 'next';
import Image from 'next/image';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import React, { useRef, useState } from 'react';

import styles from './items-form-block1440px.module.scss';
import ButtonSimple from '../HeroBlock/components/ButtonSimple/ButtonSimple';
import TextField from './components/Text-field/Text-field';
import { ItemsFormBlock1440pxType } from '@/types/itemsFormBlock.types';
import SuccessModal from '../SuccessModal/SuccessModal';
import ErrorModal from '../ErrorModal/ErrorModal';
import CheckboxForForm from '../CheckboxForForm/CheckboxForForm';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

interface IFormInputs {
  email: string;
  name: string;
  message: string;
}

const ItemsFormBlock: NextPage<ItemsFormBlock1440pxType> = ({
  title,
  description,
  items,
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<IFormInputs>({
    defaultValues: {
      email: '',
      name: '',
      message: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/aircraftSales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          comment: data.message, // Align with API expectations
          ...attribution,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      reset();
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <section className={styles.wrapper}>
      <div className="container">
        <div className={`${styles.formblock}`}>
          <div className={styles.leftcol}>
            <div className={styles.text}>
              {title && (
                <h2
                  className={styles.lookingForA}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}
              {description && (
                <div
                  className={styles.tellUsWhatContainer}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
            <div className={styles.items}>
              {items &&
                items.map((item) => (
                  <div className={styles.item11} key={item.id}>
                    <Image
                      className={styles.icon5}
                      width={24}
                      height={24}
                      sizes="100vw"
                      alt=""
                      src="/img/itemsFormBlock/round-check.svg"
                    />
                    <div className={styles.text6}>{item.title}</div>
                  </div>
                ))}
            </div>
          </div>
          <div className={styles.rightcol}>
            <form
              ref={formRef}
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className={styles.form}
            >
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Invalid email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Email"
                    placeholder="Enter your email"
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(filterCyrillicFromEmail(e.target.value))
                    }
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'Name is required',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Your name"
                    placeholder="Enter your name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <div className={styles.textField}>
                <div className={styles.label}>Your message</div>
                <Controller
                  name="message"
                  control={control}
                  rules={{
                    required: 'Message is required',
                    minLength: {
                      value: 4,
                      message: 'Message should be at least 4 characters',
                    },
                    maxLength: {
                      value: 1000,
                      message: 'Message should not exceed 1000 characters',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <textarea
                        className={`${styles.row} ${fieldState.error ? styles.error : ''}`}
                        placeholder="Enter your message (any questions or wishes)"
                        rows={4}
                        cols={32}
                        value={field.value}
                        onChange={field.onChange}
                      />
                      {fieldState.error && (
                        <div className={styles.errorText}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
              <CheckboxForForm
                consentChecked={consentChecked}
                setConsentChecked={setConsentChecked}
              />
              <div className={styles.submitButton}>
                <ButtonSimple
                  text={isSubmitting ? 'Sending...' : 'Send'}
                  designBlendingMode=""
                  designBlendingMode1=""
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </section>
  );
};

export default ItemsFormBlock;
